"use client";

import { type ReactNode } from "react";
import {
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: 1,
        // Avoid immediate client refetch of server-rendered data.
        staleTime: 60 * 1000,
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined;

/**
 * App Router–safe QueryClient accessor.
 *
 * - Server: always create a new client (do not share cache across requests).
 * - Browser: reuse a singleton so React Query state survives remounts.
 *
 * Important: do not initialize the client with `useState`. If a child suspends
 * (e.g. `useSearchParams` during prerender) without an intervening boundary,
 * React can discard that state and leave hooks without a provider.
 * @see https://tanstack.com/query/latest/docs/framework/react/guides/advanced-ssr
 */
function getQueryClient() {
  if (typeof window === "undefined") {
    return makeQueryClient();
  }

  browserQueryClient ??= makeQueryClient();
  return browserQueryClient;
}

interface ReactQueryProviderProps {
  children: ReactNode;
}

export function ReactQueryProvider({ children }: ReactQueryProviderProps) {
  const queryClient = getQueryClient();

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}
